import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { listAllEmployees, peopleGateway } from '../../people';
import type { EmployeeResponse } from '../../people/types';
import { timeAndLeaveGateway } from '../index';
import { attendanceEntryStatusLabels, AttendanceEntryStatus, type AttendanceEntryResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { AccessRestrictionNotice } from '../../../shared/AccessRestrictionNotice';
import { ApiError } from '../../identity/types';
import { useFeatureAccess } from '../../subscription/useFeatureAccess';
import { EscoHrMobilePanel } from '../components/EscoHrMobilePanel';

const today = () => new Date().toISOString().slice(0, 10);

/** Catalog key of the plan-gated manager review/approval workflow (see SubscriptionFeatureKeys in
 * EPN.Identity.Contracts). Creating, editing and viewing time entries stays available on every plan. */
const MANAGER_TIMESHEET_APPROVAL = 'manager-timesheet-approval';

export function TimeAttendancePage() {
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [entries, setEntries] = useState<AttendanceEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [workDate, setWorkDate] = useState(today());
  const [workedHours, setWorkedHours] = useState('8');
  const [overtimeHours, setOvertimeHours] = useState('0');
  const [notes, setNotes] = useState('');

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const { featureAccess, refresh: refreshFeatureAccess } = useFeatureAccess(MANAGER_TIMESHEET_APPROVAL);
  // Display convenience only — the approve/reject/reopen endpoints enforce the plan themselves. While the state
  // is loading or unknown the controls stay available and the backend decides.
  const approvalRestricted = featureAccess.status === 'resolved' && featureAccess.display !== 'enabled';
  const approvalRestrictedHint = approvalRestricted ? 'Manager timesheet approval is not available on your current plan or subscription.' : undefined;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // This picker needs the complete employee collection, not one page of it — follows every page
        // of the server-paginated Employee List query via the shared load-all helper.
        const emp = await listAllEmployees(peopleGateway, { sortBy: 'name' });
        if (cancelled) return;
        setEmployees(emp.filter((e) => e.isActive));
        const first = emp[0];
        if (first) setEmployeeId((prev) => prev || first.id);
      } catch (err) {
        if (!cancelled) setError(friendlyErrorMessage(err, 'Could not load employees.'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadEntries = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await timeAndLeaveGateway.listAttendanceEntries(employeeId);
      setEntries(list.sort((a, b) => b.workDate.localeCompare(a.workDate)));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load time entries.'));
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadEntries();
  }, [loadEntries]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await timeAndLeaveGateway.createAttendanceEntry(employeeId, {
        workDate, workedHours: Number(workedHours), overtimeHours: Number(overtimeHours), notes: notes || null,
      });
      setNotes('');
      await loadEntries();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not submit the time entry.'));
    }
  }

  /** A 403 from a protected approval action is either a plan/subscription refusal or a missing role permission.
   * Re-read the real state to tell them apart: a plan/subscription refusal is explained by the restriction
   * notice (never mislabelled "You don't have permission"); anything else keeps the standard message. */
  async function reportActionError(err: unknown, fallback: string) {
    if (err instanceof ApiError && err.status === 403) {
      const latest = await refreshFeatureAccess();
      if (latest.status === 'resolved' && latest.display !== 'enabled') {
        setError(null);
        return;
      }
    }
    setError(friendlyErrorMessage(err, fallback));
  }

  async function handleApprove(id: string) {
    setError(null);
    setBusyId(id);
    try {
      await timeAndLeaveGateway.approveAttendanceEntry(id);
      await loadEntries();
    } catch (err) {
      await reportActionError(err, 'Could not approve the time entry.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(e: FormEvent) {
    e.preventDefault();
    if (!rejectingId) return;
    setError(null);
    setBusyId(rejectingId);
    try {
      await timeAndLeaveGateway.rejectAttendanceEntry(rejectingId, rejectionReason);
      setRejectingId(null);
      setRejectionReason('');
      await loadEntries();
    } catch (err) {
      await reportActionError(err, 'Could not reject the time entry.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleReopen(id: string) {
    setError(null);
    setBusyId(id);
    try {
      await timeAndLeaveGateway.reopenAttendanceEntry(id);
      await loadEntries();
    } catch (err) {
      await reportActionError(err, 'Could not reopen the time entry.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
    <EscoHrMobilePanel />
    <section className="card card--wide">
      <div className="section-header">
        <h2>Time & Attendance</h2>
        <span className="badge badge--caution">Daily hours — no clock punches or meal breaks</span>
      </div>
      <p className="muted">
        Worked/overtime hours are entered per day, then approved or rejected. This is a real, tested
        backend (<code>EPN.TimeAndLeave.Api</code>) — see <code>docs/time-and-attendance.md</code> for what
        this does and does not model.
      </p>

      {error && <p className="error">{error}</p>}

      {featureAccess.status === 'resolved' && (
        <AccessRestrictionNotice feature={featureAccess.feature} access={featureAccess.access} />
      )}

      <label htmlFor="employee-select">Employee</label>
      <select id="employee-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
        {employees.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>)}
      </select>

      <form className="inline-form" onSubmit={handleSubmit} style={{ marginTop: 12 }}>
        <label className="inline-form-date-label">
          Work date
          <input type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} required />
        </label>
        <input type="number" step="0.25" min="0" placeholder="Worked hours" value={workedHours} onChange={(e) => setWorkedHours(e.target.value)} required />
        <input type="number" step="0.25" min="0" placeholder="Overtime hours" value={overtimeHours} onChange={(e) => setOvertimeHours(e.target.value)} />
        <input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <button type="submit" disabled={!employeeId}>Submit entry</button>
      </form>

      {rejectingId && (
        <form className="inline-form" onSubmit={handleReject} style={{ marginTop: 12 }}>
          <strong>Rejection reason</strong>
          <input value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} required />
          <button type="submit">Confirm reject</button>
          <button type="button" className="link" onClick={() => setRejectingId(null)}>Cancel</button>
        </form>
      )}

      {loading && <p className="muted">Loading…</p>}

      {!loading && (
        <table>
          <thead>
            <tr><th>Date</th><th>Worked</th><th>OT</th><th>Status</th><th>Submitted / Approved</th><th></th></tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.workDate}</td>
                <td>{entry.workedHours}</td>
                <td>{entry.overtimeHours}</td>
                <td>
                  {attendanceEntryStatusLabels[entry.status]}
                  {entry.status === AttendanceEntryStatus.Rejected && entry.rejectionReason && (
                    <div className="muted">Reason: {entry.rejectionReason}</div>
                  )}
                </td>
                <td className="muted">
                  {entry.approvedAtUtc ? `Approved ${new Date(entry.approvedAtUtc).toLocaleString()}` : '—'}
                </td>
                <td>
                  {entry.status === AttendanceEntryStatus.Submitted && (
                    <>
                      <button type="button" className="link" disabled={busyId === entry.id || approvalRestricted} title={approvalRestrictedHint} onClick={() => void handleApprove(entry.id)}>Approve</button>
                      <button type="button" className="link" disabled={busyId === entry.id || approvalRestricted} title={approvalRestrictedHint} onClick={() => setRejectingId(entry.id)}>Reject</button>
                    </>
                  )}
                  {entry.status === AttendanceEntryStatus.Approved && (
                    <button type="button" className="link" disabled={busyId === entry.id || approvalRestricted} title={approvalRestrictedHint} onClick={() => void handleReopen(entry.id)}>Reopen</button>
                  )}
                </td>
              </tr>
            ))}
            {entries.length === 0 && <tr><td colSpan={6} className="muted">No time entries for this employee yet.</td></tr>}
          </tbody>
        </table>
      )}
    </section>
    </>
  );
}
